import { APPROVE, ASSESSMENTS, BUSINESSES, BUSINESSES_BASIC, CLEAR, COMMON, CONFIGURATION, COPY, CURRENT, DDQ, FOLDERS, IRQ, POTENTIAL_PARENTS, RESTART, REVIEW, START, TOGGLE, UCRM, UNRESTRICTED, USE_CASES, VERSIONS } from 'Config/Paths';
import { AssessmentSubjectSummary, SetActiveStatesRequest, UpdateCommonAssessmentRequest } from 'Models/AssessmentSubject';
import { SaveOrSubmitFinalReviewRequest, StartAssessmentWorkflowRequest } from 'Models/DueDiligenceWorkflow';
import { CreateOrUpdateFolderRequest, FolderAllVersions, FolderTypes, FolderVersion } from 'Models/Folders';
import { InherentRiskQuestionnaireConfiguration, InherentRiskQuestionnaireResponse } from 'Models/InherentRisk';
import { ProductModule } from 'Models/ProductModule';
import { ResponseModel } from 'Models/ResponseModel';
import { Business, BusinessForNonUcrmUser, BusinessResponse, BusinessResponseWithUseCases, BusinessWithUseCasesForNonUcrmUser, CompleteUcrmIntakeRequest, CreateBusinessRequest, CreateUseCaseRequest, UcrmSaveOrSubmitInherentRiskQuestionnaireRequest, UpdateBusinessRequest, UpdateUseCaseRequest, UseCase, UseCaseForNonUcrmUser } from 'Models/Ucrm';
import { restApiDelete, restApiGet, restApiPost } from 'RestApiConnector';

import { ClientUcrmApi } from './UcrmApi';

export class AWSUcrmApi implements ClientUcrmApi {
    readonly module = ProductModule.UCRM;

    // Businesses
    createBusiness = (createBusinessRequest: CreateBusinessRequest): Promise<ResponseModel<BusinessResponse | BusinessForNonUcrmUser>> => {
        return restApiPost(`/${UCRM}/${BUSINESSES}`, createBusinessRequest);
    };

    deleteBusiness = (businessId: string): Promise<ResponseModel<void>> => {
        return restApiDelete(`/${UCRM}/${BUSINESSES}/${businessId}`);
    };

    getAllBusinessesWithBasicData = (): Promise<ResponseModel<Business[]>> => {
        return restApiGet(`/${UCRM}/${BUSINESSES_BASIC}`, {});
    };

    /** @returns A list of all Businesses and their Use Cases. The list is sorted by Business name. */
    getBusinesses = (): Promise<ResponseModel<BusinessResponseWithUseCases[]>> => {
        return restApiGet(`/${UCRM}/${BUSINESSES}`, {}).then((response): ResponseModel<BusinessResponseWithUseCases[]> => {
            return {
                ...response,
                data: response.data.sort((businessA: BusinessResponseWithUseCases, businessB: BusinessResponseWithUseCases) => businessA.name.localeCompare(businessB.name)),
            };
        });
    };

    getBusinessesForNonUcrmUser = (): Promise<ResponseModel<BusinessWithUseCasesForNonUcrmUser[]>> => {
        return restApiGet(`/${UCRM}/${UNRESTRICTED}/${BUSINESSES}`, {});
    };

    getBusinessDetails = (businessId: string): Promise<ResponseModel<BusinessResponse>> => {
        return restApiGet(`/${UCRM}/${BUSINESSES}/${businessId}`, {});
    };

    getBusinessDetailsForNonUcrmUser = (businessId: string): Promise<ResponseModel<BusinessForNonUcrmUser>> => {
        return restApiGet(`/${UCRM}/${UNRESTRICTED}/${BUSINESSES}/${businessId}`, {});
    };

    updateBusiness = (updateBusinessRequest: UpdateBusinessRequest, businessId: string): Promise<ResponseModel<BusinessResponse | BusinessForNonUcrmUser>> => {
        return restApiPost(`/${UCRM}/${BUSINESSES}/${businessId}`, updateBusinessRequest);
    };

    completeUcrmIntake = (request: CompleteUcrmIntakeRequest): Promise<ResponseModel<void>> => {
        return restApiPost(`/${UCRM}/${APPROVE}`, request);
    };

    setActiveStates = (request: SetActiveStatesRequest, businessId: string): Promise<ResponseModel<void>> => {
        return restApiPost(`/${UCRM}/${BUSINESSES}/${businessId}/${TOGGLE}`, request);
    };

    // Use Cases
    createUseCase = (request: CreateUseCaseRequest, businessId: string): Promise<ResponseModel<UseCase | UseCaseForNonUcrmUser>> => {
        return restApiPost(`/${UCRM}/${BUSINESSES}/${businessId}/${USE_CASES}`, request);
    };

    deleteUseCase = (businessId: string, useCaseId: string): Promise<ResponseModel<void>> => {
        return restApiDelete(`/${UCRM}/${BUSINESSES}/${businessId}/${USE_CASES}/${useCaseId}`);
    };

    getUseCaseDetails = (businessId: string, useCaseId: string): Promise<ResponseModel<UseCase>> => {
        return restApiGet(`/${UCRM}/${BUSINESSES}/${businessId}/${USE_CASES}/${useCaseId}`);
    };

    getUseCaseDetailsForNonUcrmUser = (businessId: string, useCaseId: string): Promise<ResponseModel<UseCaseForNonUcrmUser>> => {
        return restApiGet(`/${UCRM}/${UNRESTRICTED}/${BUSINESSES}/${businessId}/${USE_CASES}/${useCaseId}`);
    };

    /** Get Use Cases for a specific Business or all Use Cases if no Business is specified. */
    getUseCases = (businessId?: string): Promise<ResponseModel<UseCase[]>> => {
        if (businessId) {
            return restApiGet(`/${UCRM}/${BUSINESSES}/${businessId}/${USE_CASES}`);
        } else {
            return restApiGet(`/${UCRM}/${BUSINESSES}/${USE_CASES}`);
        }
    };

    copyAssessmentReviewToFinalReview = (businessId: string, useCaseId: string): Promise<ResponseModel<UseCase>> => {
        return restApiPost(`/${UCRM}/${BUSINESSES}/${businessId}/${USE_CASES}/${useCaseId}/${REVIEW}/${COPY}`, {});
    };

    saveOrSubmitFinalReview = (businessId: string, useCaseId: string, request: SaveOrSubmitFinalReviewRequest): Promise<ResponseModel<UseCase>> => {
        return restApiPost(`/${UCRM}/${BUSINESSES}/${businessId}/${USE_CASES}/${useCaseId}/${REVIEW}`, request);
    };

    updateCommonAssessment = (request: UpdateCommonAssessmentRequest): Promise<ResponseModel<void>> => {
        return restApiPost(`/${UCRM}/${ASSESSMENTS}/${COMMON}`, request);
    };

    updateUseCase = (request: UpdateUseCaseRequest, businessId: string, useCaseId: string): Promise<ResponseModel<UseCase | UseCaseForNonUcrmUser>> => {
        return restApiPost(`/${UCRM}/${BUSINESSES}/${businessId}/${USE_CASES}/${useCaseId}`, request);
    };

    // Workflow
    startNewAssessmentWorkflow = (businessId: string, useCaseId: string, request: StartAssessmentWorkflowRequest): Promise<ResponseModel<void>> => {
        return restApiPost(`/${UCRM}/${BUSINESSES}/${businessId}/${USE_CASES}/${useCaseId}/${ASSESSMENTS}/${START}`, request);
    };

    restartAssessmentWorkflow = (businessId: string, useCaseId: string, request: StartAssessmentWorkflowRequest): Promise<ResponseModel<void>> => {
        return restApiPost(`/${UCRM}/${BUSINESSES}/${businessId}/${USE_CASES}/${useCaseId}/${ASSESSMENTS}/${RESTART}`, request);
    };

    clearAssessmentWorkflow = (businessId: string, useCaseId: string): Promise<ResponseModel<void>> => {
        return restApiPost(`/${UCRM}/${BUSINESSES}/${businessId}/${USE_CASES}/${useCaseId}/${ASSESSMENTS}/${CLEAR}`, {});
    };

    // IRQ
    getInherentRiskQuestionnaireConfiguration = (): Promise<ResponseModel<InherentRiskQuestionnaireConfiguration>> => {
        return restApiGet(`/${UCRM}/${IRQ}/${CONFIGURATION}`);
    };

    setInherentRiskQuestionnaireConfiguration = (configuration: InherentRiskQuestionnaireConfiguration): Promise<ResponseModel<void>> => {
        return restApiPost(`/${UCRM}/${IRQ}/${CONFIGURATION}`, configuration);
    };

    getInherentRiskQuestionnaire = (businessId: string, useCaseId: string): Promise<ResponseModel<InherentRiskQuestionnaireResponse | undefined>> => {
        return restApiGet(`/${UCRM}/${BUSINESSES}/${businessId}/${USE_CASES}/${useCaseId}/${IRQ}`);
    };

    saveInherentRiskQuestionnaire = (businessId: string, useCaseId: string, request: UcrmSaveOrSubmitInherentRiskQuestionnaireRequest): Promise<ResponseModel<InherentRiskQuestionnaireResponse>> => {
        return restApiPost(`/${UCRM}/${BUSINESSES}/${businessId}/${USE_CASES}/${useCaseId}/${IRQ}`, request);
    };

    getCommonAssessmentPotentialParents = (businessId: string, useCaseId: string): Promise<ResponseModel<AssessmentSubjectSummary[]>> => {
        return restApiGet(`/${UCRM}/${BUSINESSES}/${businessId}/${USE_CASES}/${useCaseId}/${DDQ}/${POTENTIAL_PARENTS}/`);
    };

    // Documents/folders
    createNewFolder = (businessId: string, createNewFolderRequest: CreateOrUpdateFolderRequest): Promise<ResponseModel<string>> => {
        return restApiPost(`/${UCRM}/${BUSINESSES}/${businessId}/${FOLDERS}`, createNewFolderRequest);
    };

    createNewFolderVersion = (businessId: string, folderId: string, createNewFolderVersionRequest: CreateOrUpdateFolderRequest): Promise<ResponseModel<void>> => {
        return restApiPost(`/${UCRM}/${BUSINESSES}/${businessId}/${FOLDERS}/${folderId}/${VERSIONS}`, createNewFolderVersionRequest);
    };

    deleteFolder = (businessId: string, folderId: string): Promise<ResponseModel<void>> => {
        return restApiDelete(`/${UCRM}/${BUSINESSES}/${businessId}/${FOLDERS}/${folderId}`);
    };

    getFolderTypes = (): Promise<ResponseModel<FolderTypes>> => {
        return restApiGet(`/${UCRM}/${FOLDERS}/${CONFIGURATION}`, {});
    };

    /** Get all current Folder versions for all Businesses. */
    getAllCurrentFolders = (): Promise<ResponseModel<FolderVersion[]>> => {
        return restApiGet(`/${UCRM}/${FOLDERS}`, {});
    };

    getAllCurrentFoldersForBusiness = (businessId: string): Promise<ResponseModel<FolderVersion[]>> => {
        return restApiGet(`/${UCRM}/${BUSINESSES}/${businessId}/${FOLDERS}`, {});
    };

    getAllFolderVersions = (businessId: string, folderId: string): Promise<ResponseModel<FolderAllVersions>> => {
        return restApiGet(`/${UCRM}/${BUSINESSES}/${businessId}/${FOLDERS}/${folderId}`);
    };

    manageFolder = (businessId: string, folderId: string, createNewFolderVersionRequest: CreateOrUpdateFolderRequest): Promise<ResponseModel<void>> => {
        return restApiPost(`/${UCRM}/${BUSINESSES}/${businessId}/${FOLDERS}/${folderId}/${VERSIONS}/${CURRENT}`, createNewFolderVersionRequest);
    };

    updateFolderTypes = (folderTypes: FolderTypes): Promise<ResponseModel<void>> => {
        return restApiPost(`/${UCRM}/${FOLDERS}/${CONFIGURATION}`, folderTypes);
    };
}
